import { IImageData, ImageRecord, imageFromJSON } from "common/records/Image";
import {
  IImageOptions,
  destroy,
  create,
  update,
  ICustomerAuthOptions,
  createForCustomer,
  list,
  directUpload,
  createDirect,
} from "common/api/image.service";
import { DANGER, FlashLevelTypes, SUCCESS, addFlashAlert } from "common/components/FlashAlert";
import { RootState, ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { ImageActions } from "common/reducers/image.actions";
import { OrgActions } from "common/reducers/org.actions";
import { EstimateLineItemActions } from "common/reducers/estimateLineItem.actions";
import { RootDispatchType, store } from "common/store";
import { currentJobId } from "common/selectors/job.selectors";
import { currentOrgId } from "common/selectors/org.selectors";
import { directUploadForCustomer } from "common/api/directUpload.service";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { TaskActions } from "common/reducers/task.actions";
import { ITaskData } from "common/records/Task";
import { TaskContextOverrides } from "common/records/TaskContext";
import { ImageParamsRecord } from "common/records/index/ImageParams";
import { List } from "immutable";
import { watchFolderParentId } from "src/router/loaders/helper";
import { FolderAsyncActions } from "common/reducers/folder.async.actions";
import { folderParamsFromJSON } from "common/records/index/FolderParams";
import { image } from "common/selectors/image.selectors";
import { fileFromJSON, IFileData } from "common/records/File";
import { TokenActions } from "common/reducers/token.actions";
import { manufacturerIndex } from "common/selectors/tokenFeneTech.selectors";
import { ids } from "common/selectors/idsManagement.selectors";
import { ITaskChannelService } from "common/api/websockets/TaskChannel.service";
import { ProductOptionActions } from "common/reducers/productOption.action";
import { exportFileToS3 } from "common/helpers/File";

export const ImageAsyncActions = {
  listImages: (queryParams: ImageParamsRecord, refreshing = false): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      if (!refreshing) dispatch(IdsManagementActions.fetchModel("images", queryParams));

      if (!queryParams.folder_id) {
        const folderId = await watchFolderParentId("Photos", "Job");
        queryParams = queryParams.set("folder_id", folderId);
      }

      if (queryParams.imageableType === "Job" && !refreshing)
        dispatch(
          FolderAsyncActions.listFolders(
            folderParamsFromJSON({
              id: queryParams.folder_id,
              folderable_type: queryParams.imageableType,
              folderable_id: queryParams.imageableId,
            }),
          ),
        );
      try {
        const response = await list(queryParams);
        const ids = List(response.images.map((image) => image.id));

        dispatch(ImageActions.receiveImages(response.images));
        dispatch(IdsManagementActions.receiveModel("images", ids, response.meta.pagination));
      } catch (response) {
        handleErrors(response);
        dispatch(IdsManagementActions.setModelLoaded("images"));
      }
    };
  },
  destroyImage: (image: ImageRecord): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(IdsManagementActions.fetchModel("imagesUpdate"));

      try {
        await destroy(image);
        dispatch(ImageActions.destroyImage(image));
        dispatch(IdsManagementActions.removeId(image.id, "images"));

        addFlashAlert("success", "Image successfully deleted");
        if (image.imageable_type?.toLowerCase() === "org") {
          dispatch(OrgActions.setOrgImage(null, image.display));
        }
      } catch (response) {
        handleErrors(response);
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("imagesUpdate"));
      }
    };
  },
  batchCreateImage: (
    files: any[],
    options: IImageOptions,
    notify = true,
    taskContextOverrides: TaskContextOverrides = {},
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      dispatch(ImageActions.setImageLoading(true));
      options = setOptionsId(options, getState());
      const { replacedNegativeIds } = options;
      let imageSuccess = 0;
      await Promise.all(
        files.map(async (file, index) => {
          const record = imageFromJSON({
            name: file.name,
            display: options.display || "do_not_display",
            file_queue: file,
            imageable_id: options.imageableId as number,
            imageable_type: options.imageableType,
            folder_id: options.folder_id,
            sort_order: options.sort_order || 0,
          });
          try {
            const response = await create(record);
            let succeeded = false;
            if (Array.isArray(response.images)) {
              dispatch(ImageActions.receiveImages(response.images));
              response.images.forEach((image) => {
                dispatch(ImageAsyncActions.processImage(image, taskContextOverrides));
              });
              succeeded = response.images.length > 0;
            } else if (response.image) {
              dispatch(ImageActions.receiveImage(response.image));
              dispatch(ImageAsyncActions.processImage(response.image, taskContextOverrides));
              succeeded = true;
            }
            // Empty success response = server accepted but created nothing; keep the
            // staging record so the next save retries.
            if (!succeeded) return;

            imageSuccess += 1;

            // Phase 3 cleanup: drop the staging record now that the server minted the real one.
            const replacedId = replacedNegativeIds?.[index];
            if (replacedId !== undefined && replacedId < 0) {
              if (options.imageableType === "EstimateLineItem" && options.imageableId) {
                dispatch(EstimateLineItemActions.removeImageId(options.imageableId, replacedId));
              }
              dispatch(ImageActions.removeImage(replacedId));
            }
          } catch (response) {
            handleErrors(response, notify);
          }
        }),
      );

      if (notify) {
        const flashLevel: Exclude<FlashLevelTypes, "update"> = imageSuccess === files.length ? "success" : "danger";
        addFlashAlert(flashLevel, imageSuccess + "/" + files.length + " Images successfully uploaded");
      }
      dispatch(ImageActions.setImageLoading(false));
    };
  },

  batchCreateCustomerImages: (
    files: any[],
    folderId: number,
    uploadOptions: ICustomerAuthOptions,
    service: ITaskChannelService,
  ): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        dispatch(IdsManagementActions.fetchModel("images"));
        dispatch(ImageActions.resetCount());
        dispatch(ImageActions.setCustomerImagesError(false));
        const fileCount = files.length;
        const { urls } = await directUploadForCustomer(fileCount, "image", uploadOptions);

        const batchSize = 5;
        const batches = Math.ceil(urls.length / batchSize);

        for (let i = 0; i < batches; i++) {
          const batchUrls = urls.slice(i * batchSize, (i + 1) * batchSize);
          const batchFiles = files.slice(i * batchSize, (i + 1) * batchSize);
          await Promise.all(
            batchUrls.map(async (obj, index) => {
              try {
                const file = batchFiles[index];
                await exportFileToS3(obj.url, file);

                const record = imageFromJSON({
                  uuid: obj.id,
                  name: file.name,
                  display: "do_not_display",
                  folder_id: folderId,
                });
                const response = await createForCustomer(record, uploadOptions);
                if (response?.image) {
                  dispatch(ImageAsyncActions.processCustomerImage(response.image, service));
                }
              } catch (response) {
                dispatch(ImageActions.setCustomerImagesError(true));
                handleErrors(response, false);
              }
            }),
          );
        }
        dispatch(IdsManagementActions.setModelLoaded("images"));
      } catch (response) {
        dispatch(ImageActions.setCustomerImagesError(true));
        handleErrors(response, false);
      }
    };
  },

  processCustomerImage: (image: IImageData, service: ITaskChannelService): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      const { task } = image;
      if (task) {
        service.connectTask(task.id);
        const context = {
          model: "image",
          action: "processImageTaskHandler",
          action_payload: { id: image.id },
          error_action: "customerErrorTaskHandler",
        };
        task.context = context;
        dispatch(ImageActions.receiveImage(image));
        dispatch(TaskActions.receiveTask(task));
        dispatch(ImageActions.receiveCustomerImageStatusByTaskId(task));
      }
    };
  },

  processImageTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(ImageActions.updateImageWithUrls(payload.id, task.results));
      dispatch(IdsManagementActions.pushId(payload.id, "images"));
      dispatch(ImageActions.receiveCustomerImageStatusByTaskId(task));
      dispatch(ImageActions.increaseCount());
    };
  },
  customerErrorTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(ImageActions.setCustomerImagesError(true));
      dispatch(ImageActions.receiveCustomerImageStatusByTaskId(task));
    };
  },

  processImage: (image: Partial<IImageData>, overrides: TaskContextOverrides = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      if (image.imageable_type === "Job" && image.id) {
        dispatch(IdsManagementActions.pushId(image.id, "images"));
      }
      if (image.imageable_type === "Org") {
        dispatch(OrgActions.setOrgImage(image.id, image.display));
      }
      if (image.imageable_type === "ProductOption" && image.id) {
        dispatch(ProductOptionActions.pushImageId(image.id));
      }
      if (image.imageable_type === "EstimateLineItem" && image.id && image.imageable_id) {
        dispatch(EstimateLineItemActions.pushImageId(image.imageable_id, image.id));
      }
      const { task } = image;
      if (task) {
        const context = {
          model: "image",
          action: overrides.action ?? "directUploadImageTaskHandler",
          action_payload: {
            id: image.id,
            // Threaded so error handlers can clean up the parent's id reference when
            // the task fails — without these, removing from images.byId leaves a
            // dangling id on estimateLineItems.byId[N].image_ids. Mirrors the doc
            // pattern in processDocument.
            imageable_type: image.imageable_type,
            imageable_id: image.imageable_id,
            ...(overrides.action_payload ?? {}),
          },
          error_action: overrides.error_action ?? "errorTaskHandler",
        };
        task.context = context;
        dispatch(TaskActions.receiveTask(task));
      }
    };
  },
  /**
   * Signature mirrors `batchCreateImage` (notify + taskContextOverrides) so the estimate save flow can
   * drive it silently (notify=false + SILENT_IMAGE_CONTEXT) while line-item file uploads run on the
   * reliable direct-to-S3 path (presign → S3 PUT → lightweight `createDirect`). See RSF-25794 BR-4.
   *
   * @param files - the files to upload
   * @param options - image options (imageableType/imageableId, sort_order, replacedNegativeIds, etc.)
   * @param notify - false to suppress the success/failure flash (used by the save flow)
   * @param taskContextOverrides - NewRelic task-context overrides for the upload
   * @returns the thunk's Promise, resolving once every file has been processed
   */
  batchCreateDirectUploadImages: (
    files: any[],
    options: IImageOptions,
    notify = true,
    taskContextOverrides: TaskContextOverrides = {},
  ): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      dispatch(ImageActions.setImageLoading(true));
      options = setOptionsId(options, getState());
      const { replacedNegativeIds } = options;
      try {
        const { imageableType, imageableId } = options;
        const fileCount = files.length;
        const { urls } = await directUpload(fileCount, imageableType, imageableId as number);

        let imagesSuccess = 0;
        // Iterate `files` (not `urls`) so `index` indexes the file/staging-id pair — replacedNegativeIds
        // must align with files[index]. A short urls array skips its surplus files cleanly, leaving
        // their staging records for the next save.
        await Promise.all(
          files.map(async (file, index) => {
            const obj = urls[index];
            if (!obj) return;
            try {
              await exportFileToS3(obj.url, file);

              const record = imageFromJSON({
                name: file.name,
                display: options.display || "do_not_display",
                imageable_id: options.imageableId as number,
                imageable_type: options.imageableType,
                folder_id: options.folder_id,
                // Persist upload order (BR-3) — the staged line-item record's sort_order threads
                // through here. Callers that don't stage an order (Job photos, elevations, generic
                // ImageInput) pass no sort_order → null → cleanProps omits it → the server assigns
                // the next value via Sortable. Do NOT default to 0: a literal 0 survives cleanProps
                // and would pin every such upload to 0, collapsing server-side ordering.
                sort_order: options.sort_order ?? null,
                uuid: obj.id,
              });
              const response = await createDirect(record);

              // The data-URL preview is cosmetic; a FileReader failure must not fail an already-persisted
              // upload — that would strand the staging record and duplicate the image on the next save.
              let previewUrl: string | undefined;
              try {
                previewUrl = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result as string);
                  reader.onerror = reject;
                  reader.readAsDataURL(file);
                });
              } catch {
                previewUrl = undefined;
              }

              dispatch(
                ImageActions.receiveImage({
                  ...response.image,
                  ...(previewUrl ? { file: { small: { url: previewUrl } } as IFileData } : {}),
                } as Partial<IImageData>),
              );
              dispatch(ImageAsyncActions.processImage(response.image as Partial<IImageData>, taskContextOverrides));

              // Drop the staging record now that the server minted the real one. Only reached on
              // success — a failed PUT/create falls to the catch below, leaving the staging record in
              // place so the caller (save Phase 3) detects the failure and the next save retries.
              const replacedId = replacedNegativeIds?.[index];
              if (replacedId !== undefined && replacedId < 0) {
                if (options.imageableType === "EstimateLineItem" && options.imageableId) {
                  dispatch(EstimateLineItemActions.removeImageId(options.imageableId, replacedId));
                }
                dispatch(ImageActions.removeImage(replacedId));
              }

              imagesSuccess += 1;
              return response;
            } catch (response) {
              return response;
            }
          }),
        );

        if (notify) {
          const flashLevel: Exclude<FlashLevelTypes, "update"> = imagesSuccess === files.length ? "success" : "danger";
          addFlashAlert(flashLevel, imagesSuccess + "/" + files.length + " Images successfully uploaded");
        }
      } catch (response) {
        handleErrors(response, notify);
      } finally {
        dispatch(ImageActions.setImageLoading(false));
      }
    };
  },
  errorTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      const errorMessage =
        task?.results?.error_message ||
        "There were problems processing your task. Please try again. If the problem persists contact support.";

      addFlashAlert(DANGER, errorMessage);

      removeImageFromOwner(dispatch, payload);
      dispatch(ImageActions.setImageLoading(false));
    };
  },
  directUploadImageTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(ImageActions.editImage(payload.id, "file", fileFromJSON(task.results.customer_image_file)));

      if (payload.manufacturerId) {
        dispatch(TokenActions.addImageId(payload.orgId, payload.kind, payload.id));
        const index = manufacturerIndex(store.getState(), {
          recordId: payload.manufacturerId,
          editType: payload.editType,
        });
        let rootName = "manufacturers";
        if (payload.editType === "available" || payload.editType === "add") {
          rootName = "availableManufacturers";
        }
        const imageUuid = image(store.getState(), { imageId: payload.id })?.uuid;
        dispatch(TokenActions.editToken(payload.orgId, payload.kind, ["data", rootName, index, "image"], imageUuid));
      }

      const name = task.results.class_name || "Task Completion";
      addFlashAlert(SUCCESS, `${name} Successful.`);
      dispatch(ImageActions.setImageLoading(false));
    };
  },
  silentDirectUploadImageTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(ImageActions.editImage(payload.id, "file", fileFromJSON(task.results.customer_image_file)));
      dispatch(ImageActions.setImageLoading(false));
    };
  },
  silentErrorTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      removeImageFromOwner(dispatch, payload);
      dispatch(ImageActions.setImageLoading(false));
    };
  },

  updateImage: (imageId: number, isSingleUpdate = true): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      const record = image(getState(), { imageId });
      if (!record) {
        addFlashAlert("danger", "Image not found");
        return;
      }

      if (isSingleUpdate) dispatch(IdsManagementActions.fetchModel("imagesUpdate"));
      try {
        const response = await update(record);
        if (isSingleUpdate) addFlashAlert("success", "Saved image successfully");

        if (response.image) dispatch(ImageActions.receiveImage(response.image));
      } catch (error) {
        handleErrors(error);
      } finally {
        if (isSingleUpdate) dispatch(IdsManagementActions.setModelLoaded("imagesUpdate"));
      }
    };
  },

  batchUpdateImages: (imageIds: List<number>): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(IdsManagementActions.fetchModel("imagesUpdate"));

      await Promise.all(imageIds.map((imageId) => dispatch(ImageAsyncActions.updateImage(imageId, false))));

      dispatch(IdsManagementActions.setModelLoaded("imagesUpdate"));
      addFlashAlert("success", "Images updated");
    };
  },

  moveImages: (folderId: number, modelName = "images"): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType, getState) => {
      const imageIds = ids(getState(), { modelName }) as List<number>;

      dispatch(IdsManagementActions.fetchModel(modelName));
      dispatch(ImageActions.editImages(imageIds, "folder_id", folderId));

      await dispatch(ImageAsyncActions.batchUpdateImages(imageIds));

      imageIds.forEach((imageId) => {
        dispatch(IdsManagementActions.removeId(imageId, "images"));
      });
      dispatch(IdsManagementActions.receiveModel(modelName, List()));
    };
  },
};

/**
 * Cleanup helper for task error handlers. Removes the image's id from its
 * owning entity (currently EstimateLineItem only) before deleting the
 * images.byId record. Imageable info comes from the task's action_payload,
 * which `processImage` threads through. Mirrors `removeDocumentFromOwner`.
 *
 * Known limitation: Job / Org / ProductOption owners track images via their
 * own slices (`idsManagement.byModelName.images`, `OrgRecord.image`,
 * `ProductOption.image_ids`). On task error for those owners, the image
 * record is removed from `byId` here but the parent slice still references
 * the deleted id. The doc side has the same gap. Worth a follow-up ticket
 * if it surfaces in practice.
 */
const removeImageFromOwner = (
  dispatch: RootDispatchType,
  payload: { id: number; imageable_type?: string; imageable_id?: number },
): void => {
  if (payload.imageable_type === "EstimateLineItem" && payload.imageable_id) {
    dispatch(EstimateLineItemActions.removeImageId(payload.imageable_id, payload.id));
  }
  dispatch(ImageActions.removeImage(payload.id));
};

export const SILENT_IMAGE_CONTEXT: TaskContextOverrides = {
  action: "silentDirectUploadImageTaskHandler",
  error_action: "silentErrorTaskHandler",
};

const setOptionsId = (options: Partial<IImageOptions>, state: RootState): IImageOptions => {
  if (!options.imageableId) {
    switch (options.imageableType) {
      case "Job":
        options.imageableId = currentJobId(state);
        break;
      case "Org":
        options.imageableId = currentOrgId(state);
        break;
      default:
    }
  }

  return options as IImageOptions;
};
